import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { convertFileToFolder, convertFolderToFile } from './actions';
import { file2FolderConversionBuilder, parent2FileConversionBuilder } from './actions';
import { genWorkspaceHeader } from './../../utils/constants';
import { Notification } from '../../components/Notification';
import buildBitBucketTree from '../../utils/bitbucket';
import clientService from '../../apiClient';
import {
  FILE_URL,
  FOLDER_URL,
  ORGANIZATION_URL,
  SCM_FILE_URL,
  SCM_FOLDER_URL,
  USER_ORG_MAPPING_URL,
  WORKSPACE_URL,
  UPLOAD_OPENAPI,
  STARRED_FILE_URL,
  USER_RECENT_ACTIVITY,
  WORKSPACE_API_URL,
} from '../../utils/apiEndpoints';
import {
  createFileRecursively,
  createFolderRecursively,
  fileStatus,
  saveFileRecursively,
  updateFilesInRecursiveFolder,
  constructFolderAndFileMappings,
  findFileInRecursiveFolder,
  updateFolderRecursively,
  sortFilesBySidebarPosition,
  isFolder,
  removeItemById,
  findFileInTargetFolder,
  isOpenApiFile,
  updateFileAndMaybeFolder,
} from '../../utils/common';
import { message } from 'antd';
import { DOCUMENTS_TYPE } from '../../constants/common';

const initialState = {
  loading: {
    fetchAllWorkspaces: false,
    fetchAllFolders: false,
    fetchAllFiles: false,
    createWorkspace: false,
    createFolder: false,
    createFile: false,
    updateWorkspace: false,
    updateFolder: false,
    updateFile: false,
    addOrganization: false,
    fetchOrganization: false,
    fetchUserOrgMapping: false,
    createUserOrgMapping: false,
    fetchOrganizationList: false,
    fetchBitbucketFile: false,
    fetchFileById: false,
    updateBitbucketFiles: false,
    fetchBitbucketFilesInFolder: false,
    uploadOpenApiToServer: false,
    updatePublicationForWorkspace: false,
    updateOpenApiFileToServer: false,
    deleteWorkspace: false,
    paginatedWorkspaceList: false,
    enableCIOpenApi: false,
    filePositionError: false,
    fetchStarredFile: false,
    fetchPrivateWorkspaceLoader: false,
    moveFileToTrash: false,
    deleteFolder: false,
    moveFileToDestination: false,
    fetchWorkspaceByType: false,
    filesResult: false,
    ownedFilesResult: false,
    recentlyEditedFilesResult: false,
    recentWorkspaces: false,
    starredWorkspace: false,
  },
  folderMappings: null,
  userOrganization: null,
  workspaceList: [],
  paginatedWorkspaceList: [],
  privateWorkspaceList: [],
  publicWorkspaceList: [],
  workspaceFoldersList: [],
  workspaceFilesList: [],
  organizationList: [],
  organizations: [],
  activeFile: {},
  activeWorkspace: {},
  hasMore: true,
  moveFolderList: [],
  fileDetail: {},
  prevFileOnLoadFetched: '',
  prevFolderOnLoadFetched: '',
  isMobile: false,
  starredFile: [],
  selectedOpenApiEndpoint: null,
  selectedOpenApiPath: null,
  openApimethod: null,
  openApispec: {},
  collapseLeftBar: false,
  leftbarWidth: 273,
  newWorkspaceList: {},
  fetchWorkspaceByTypeData: {},
  workspaceType: '',
  activeFolder: {},
  lastFileSaveAt: null,
  filesResult: [],
  ownedFilesResult: [],
  recentlyEditedFilesResult: [],
  isAutoCollapseLeftbar: false,
  requestAccessError: false,
  recentWorkspaces: {},
  starredWorkspace: {},
};

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    addActiveWorkspaceIfNotPresent: (state, action) => {
      const workspaceType = action?.payload?.type;
      const typeWorkspaces = state.newWorkspaceList[workspaceType] || [];
      if (!typeWorkspaces.find((ws) => ws?.id === action?.payload?.id)) {
        typeWorkspaces.push(action?.payload);
        state.newWorkspaceList[workspaceType] = typeWorkspaces;
      }
    },

    setActiveWorkspaceAction: (state, action) => {
      const findActiveWorkspace = state.workspaceList.find((ws) => ws?.id === action?.payload);
      state.activeWorkspace = findActiveWorkspace;
      state.workspaceFoldersList = [];
      state.activeFile = null;
      state.lastFileSaveAt = null;
      state.filesResult = [];
      state.ownedFilesResult = [];
      state.recentlyEditedFilesResult = [];
      state.starredFile = [];
    },

    setIsMobile: (state, action) => {
      state.isMobile = action?.payload;
    },
    removeFileAfterMoving: (state, action) => {
      state.workspaceFoldersList = removeItemById(current(state.workspaceFoldersList), action.payload);
    },
    setActiveFileNull: (state, action) => {
      state.activeFile = null;
      state.lastFileSaveAt = null;
    },
    setPrevFileOnLoadFetched: (state, action) => {
      state.prevFileOnLoadFetched = action.payload;
    },
    setPrevFolderOnLoadFetched: (state, action) => {
      state.prevFolderOnLoadFetched = action.payload;
    },
    updateSelectionPathOpenApi: (state, action) => {
      const { method, path } = action.payload;
      state.selectedOpenApiEndpoint = method && path ? `${method}-${path}` : null;
      state.selectedOpenApiPath = path;
      state.openApimethod = method;
    },
    updateOpenApiSpec: (state, action) => {
      state.openApispec = { ...state.openApispec, [action.payload.id]: action.payload.spec };
    },
    setCollapseLeftBar: (state, action) => {
      state.collapseLeftBar = action.payload;
    },
    setIsAutoCollapseLeftbar: (state, action) => {
      state.isAutoCollapseLeftbar = action.payload;
      localStorage.setItem('isAutoCollapseLeftbar', action.payload);
    },
    setLeftbarWidth: (state, action) => {
      state.leftbarWidth = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllWorkspaces.pending, (state) => {
      state.loading.fetchAllWorkspaces = true;
    });
    builder.addCase(fetchAllWorkspaces.fulfilled, (state, action) => {
      state.loading.fetchAllWorkspaces = false;
      state.workspaceList = Object.values(action?.payload?.data).flat();
    });
    builder.addCase(fetchAllWorkspaces.rejected, (state, action) => {
      state.loading.fetchAllWorkspaces = false;
      Notification.error('Unable to fetch workspaces', action?.payload?.error);
    });

    builder.addCase(fetchWorkspaceByType.pending, (state) => {
      state.loading.fetchWorkspaceByType = true;
    });
    builder.addCase(fetchWorkspaceByType.fulfilled, (state, action) => {
      const { workspaces, total_count } = action.payload?.data;
      const type = action.meta.arg?.type;
      const typeWorkspaces = state.newWorkspaceList[type] || [];
      const existingIds = new Set(typeWorkspaces.map((ws) => ws.id));

      const finalWorkspaces = type.toUpperCase() === 'PRIVATE' ? workspaces : workspaces[type];

      const updatedWorkspaces = [...typeWorkspaces, ...finalWorkspaces?.filter((ws) => !existingIds.has(ws.id))];
      state.newWorkspaceList = { ...state.newWorkspaceList, [type]: updatedWorkspaces };
      state.fetchWorkspaceByTypeData = {
        ...state.fetchWorkspaceByTypeData,
        [type]: { total_count: total_count || updatedWorkspaces.length },
      };
      state.loading.fetchWorkspaceByType = false;
    });

    builder.addCase(fetchWorkspaceByType.rejected, (state, action) => {
      state.loading.fetchWorkspaceByType = false;
      Notification.error('Unable to fetch workspaces', action?.payload?.error);
    });

    builder.addCase(fetchSpecficFolder.pending, (state) => {
      state.loading.fetchSpecficFolder = true;
    });
    builder.addCase(fetchSpecficFolder.fulfilled, (state, action) => {
      state.loading.fetchSpecficFolder = false;

      if (state.activeWorkspace?.is_git_based) {
        state.moveFolderList = buildBitBucketTree([], action?.payload?.data);
      } else {
        state.moveFolderList = [...(action?.payload?.data?.folders || [])];
      }
    });
    builder.addCase(fetchAllFolders.pending, (state) => {
      state.loading.fetchAllFolders = true;
    });
    builder.addCase(fetchAllFolders.fulfilled, (state, action) => {
      state.loading.fetchAllFolders = false;

      if (state.activeWorkspace?.is_git_based) {
        state.workspaceFoldersList = buildBitBucketTree([], action?.payload?.data);
      } else {
        const sortedFiles = sortFilesBySidebarPosition(action.payload.data);
        state.workspaceFoldersList = [...(action?.payload?.data?.folders || []), ...(sortedFiles.files || [])];
        state.activeWorkspace = action?.payload?.data;
      }
      state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
    });

    builder.addCase(fetchAllFolders.rejected, (state, action) => {
      state.loading.fetchAllFolders = false;
      state.requestAccessError = action?.payload?.error?.includes('No permissions found');
      Notification.error('Unable to fetch folders', action?.payload?.error);
    });

    builder.addCase(fetchAllFiles.pending, (state) => {
      state.loading.fetchAllFiles = true;
    });

    builder.addCase(fetchAllFiles.fulfilled, (state, action) => {
      state.loading.fetchAllFiles = false;
      state.activeFolder = action?.payload?.data;
      state.workspaceFoldersList = updateFilesInRecursiveFolder(state?.workspaceFoldersList, action?.payload?.data);
      state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
    });

    builder.addCase(fetchAllFiles.rejected, (state, action) => {
      state.loading.fetchAllFiles = false;
    });
    builder.addCase(fetchStarredFiles.pending, (state) => {
      state.loading.fetchStarredFile = true;
    });
    builder.addCase(fetchStarredFiles.fulfilled, (state, action) => {
      state.loading.fetchStarredFile = false;
      state.starredFile = action.payload.data;
    });
    builder.addCase(fetchStarredFiles.rejected, (state, action) => {
      state.loading.fetchStarredFile = false;

      Notification.error('Unable to fetch starred files', action?.payload?.error);
    });

    builder.addCase(addStarredFile.rejected, (state, action) => {
      Notification.error('Unable to star files', action?.payload?.error);
    });
    builder.addCase(unstarFile.rejected, (state, action) => {
      Notification.error('Unable to unstar files', action?.payload?.error);
    });
    builder.addCase(createWorkspace.pending, (state) => {
      state.loading.createWorkspace = true;
    });
    builder.addCase(createWorkspace.fulfilled, (state, action) => {
      state.loading.createWorkspace = false;
    });
    builder.addCase(createWorkspace.rejected, (state, action) => {
      state.loading.createWorkspace = false;
      Notification.error('Unable to create workspace', action?.payload?.error);
    });
    builder.addCase(createFolder.pending, (state) => {
      state.loading.createFolder = true;
    });
    builder.addCase(createFolder.fulfilled, (state, action) => {
      state.loading.createFolder = false;
      if (!action?.payload?.data?.parent_id_id) {
        state.workspaceFoldersList.unshift(action?.payload?.data);
      } else {
        state.workspaceFoldersList = createFolderRecursively(state?.workspaceFoldersList, action?.payload?.data);
      }
      state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
    });
    builder.addCase(createFolder.rejected, (state, action) => {
      state.loading.createFolder = false;
      Notification.error('Error happened while creating folder', action?.payload?.error);
    });
    builder.addCase(createFile.pending, (state) => {
      state.loading.createFile = true;
    });
    builder.addCase(createFile.fulfilled, (state, action) => {
      const { fileRes, convertedFileToFolder, conversionRes } = action.payload;
      const foldersTree = current(state.workspaceFoldersList);
      const { foldersList } = file2FolderConversionBuilder({
        foldersTree,
        conversionRes,
        movedItemRes: fileRes,
        convertedFileToFolder,
        isSourceFile: true,
      });
      state.workspaceFoldersList = [...foldersList];
      state.activeFile = fileRes;
      state.lastFileSaveAt = fileRes?.updated_at;
      state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
      state.loading.createFile = false;
    });
    builder.addCase(createFile.rejected, (state, action) => {
      state.loading.createFile = false;
      Notification.error('Error happened while creating file', action?.payload?.error);
    });
    builder.addCase(copyFileToWorkspace.fulfilled, (state, action) => {
      state.loading.createFile = false;
      Notification.success('File Successfully Copied');

      state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
    });
    builder.addCase(copyFileToWorkspace.rejected, (state, action) => {
      state.loading.createFile = false;

      Notification.error('Unable to copy file', action?.payload?.error);
    });
    builder.addCase(updateWorkspaceType.pending, (state) => {
      state.loading.updateWorkspace = true;
    });
    builder.addCase(updateWorkspaceType.fulfilled, (state, action) => {
      state.loading.updateWorkspace = false;

      // Extract the data from the payload
      const { id, type: newType } = action.payload.data;

      for (const category in state.newWorkspaceList) {
        const categoryList = state.newWorkspaceList[category];

        const workspaceIndex = categoryList.findIndex((workspace) => workspace.id === id);

        if (workspaceIndex !== -1) {
          const [removedWorkspace] = categoryList.splice(workspaceIndex, 1);

          if (!state.newWorkspaceList[newType]) {
            state.newWorkspaceList[newType] = [];
          }

          state.newWorkspaceList[newType].push(removedWorkspace);

          break;
        }
      }
      // state.activeWorkspace = action.payload.data;
      state.workspaceList = state.workspaceList.map((ws) => {
        if (ws.id === action?.payload?.data?.id) {
          return {
            ...action?.payload?.data,
          };
        } else {
          return ws;
        }
      });
      // state.workspaceFoldersList = [];
    });

    builder.addCase(updateWorkspace.pending, (state) => {
      state.loading.updateWorkspace = true;
    });
    builder.addCase(updateWorkspace.fulfilled, (state, action) => {
      state.loading.updateWorkspace = false;
      state.workspaceList = state.workspaceList.map((ws) => {
        if (ws.id === action?.payload?.data?.id) {
          return {
            ...action?.payload?.data,
          };
        } else {
          return ws;
        }
      });
      state.paginatedWorkspaceList = state.paginatedWorkspaceList.map((ws) => {
        if (ws.id === action?.payload?.data?.id) {
          return {
            ...action?.payload?.data,
          };
        } else {
          return ws;
        }
      });
      const { id, title, type } = action.payload.data;
      if (state.newWorkspaceList[type] && Array.isArray(state.newWorkspaceList[type])) {
        const workspaceIndex = state.newWorkspaceList[type].findIndex((workspace) => workspace.id === id);
        if (workspaceIndex !== -1) {
          state.newWorkspaceList[type][workspaceIndex] = action?.payload?.data;
        }
      }
    });
    builder.addCase(fetchPrivateWorkspace.pending, (state) => {
      state.loading.fetchPrivateWorkspaceLoader = true;
    });
    builder.addCase(fetchPrivateWorkspace.fulfilled, (state, action) => {
      state.loading.fetchPrivateWorkspaceLoader = false;

      state.newWorkspaceList.PRIVATE = action.payload.workspaces;
    });
    builder.addCase(fetchPrivateWorkspace.rejected, (state, action) => {
      state.loading.fetchPrivateWorkspaceLoader = false;
      Notification.error('Error happened while fetching private workspace', action?.payload?.error);
    });
    builder.addCase(updateWorkspace.rejected, (state, action) => {
      state.loading.updateWorkspace = false;
      Notification.error('Error happened while updating workspace', action?.payload?.error);
    });
    builder.addCase(updateFolder.pending, (state) => {
      state.loading.updateFolder = true;
    });
    builder.addCase(updateFolder.fulfilled, (state, action) => {
      state.loading.updateFolder = false;
      const foldersList = [...state?.workspaceFoldersList];

      state.workspaceFoldersList = updateFolderRecursively(foldersList, action?.payload?.data);
      state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
    });

    builder.addCase(updateFolder.rejected, (state, action) => {
      state.loading.updateFolder = false;
      Notification.error('Error happened while updating folder', action?.payload?.error);
    });
    builder.addCase(updateFile.pending, (state) => {
      state.loading.updateFile = true;
    });
    builder.addCase(updateFile.fulfilled, (state, action) => {
      const { autosave, updateTitleOnly, updateActiveFile, ...response } = action?.payload;
      state.loading.updateFile = false;
      const updatedFileData = response?.data;

      state.workspaceFoldersList = saveFileRecursively(current(state?.workspaceFoldersList), updatedFileData);
      if (updatedFileData?.status === fileStatus.REMOVED && state.activeFile?.id === updatedFileData?.id) {
        state.activeFile = null;
        state.lastFileSaveAt = null;
      } else if (!autosave && updatedFileData?.status !== fileStatus.REMOVED && updateActiveFile) {
        if (state.activeFile && updateTitleOnly) state.activeFile.title = response?.data?.title;
        else state.activeFile = response?.data;
        state.lastFileSaveAt = response?.data?.updated_at;
      }
    });
    builder.addCase(updateFile.rejected, (state, action) => {
      state.loading.updateFile = false;
      Notification.error('Error happened while updating file', action?.payload?.error);
    });

    builder.addCase(moveFileToWorkspace.fulfilled, (state, action) => {
      state.loading.updateFile = false;
      state.workspaceFoldersList = removeItemById(current(state.workspaceFoldersList), action?.payload?.data?.id);

      if (state?.activeFile?.id === action?.payload?.data?.id) {
        state.activeFile = null;
        state.lastFileSaveAt = null;
      }
      state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
    });
    builder.addCase(moveFileToWorkspace.rejected, (state, action) => {
      state.loading.updateFile = false;

      Notification.error('Unable to move File', 'Please Try again');
    });
    builder.addCase(moveFolderToWorkspace.fulfilled, (state, action) => {
      state.loading.updateFile = false;
      state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
      Notification.success('Moved Folder Succesfully');
    });

    builder.addCase(updateSidebarPosition.fulfilled, (state, action) => {
      state.loading.filePositionError = false;
      // add a function the will update the meta_info for parent folder
    });

    builder.addCase(deleteFolder.pending, (state) => {
      state.loading.deleteFolder = true;
    });

    builder.addCase(deleteFolder.fulfilled, (state, action) => {
      const { conversionData, convertedParentToFile, convertedFolderId, deleteFolderRes, isActiveFileDeleted } =
        action.payload;
      const foldersTree = current(state.workspaceFoldersList);
      const { foldersList, isParentFolder, parentInfo } = parent2FileConversionBuilder({
        foldersTree,
        conversionData,
        movedRes: deleteFolderRes,
        convertedParentToFile,
        isDeleteEvent: true,
        convertedFolderId,
      });
      state.workspaceFoldersList = foldersList;
      if (isActiveFileDeleted) {
        state.activeFile = null;
        state.lastFileSaveAt = null;
      }
      if (convertedParentToFile && parentInfo) {
        if (!isParentFolder) state.activeWorkspace.meta_info = parentInfo?.meta_info;
      }
      state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
      state.loading.deleteFolder = false;
    });

    builder.addCase(deleteFolder.rejected, (state, action) => {
      state.loading.deleteFolder = false;
      message.error('Error happened while deleting folder', action?.payload?.error);
    });

    builder.addCase(moveFileToTrash.pending, (state) => {
      state.loading.moveFileToTrash = true;
    });

    builder.addCase(moveFileToTrash.fulfilled, (state, action) => {
      const { isSameFileRemoved, convertedParentToFile, conversionRes, fileRes, convertedFolderId } = action.payload;
      const foldersTree = current(state.workspaceFoldersList);
      const { parentInfo, isParentFolder, foldersList } = parent2FileConversionBuilder({
        foldersTree,
        movedRes: fileRes,
        conversionData: conversionRes,
        convertedParentToFile,
        isDeleteEvent: true,
        convertedFolderId,
      });
      state.workspaceFoldersList = [...foldersList];
      if (isSameFileRemoved) {
        state.activeFile = null;
        state.lastFileSaveAt = null;
      }
      if (convertedParentToFile && parentInfo) {
        if (!isParentFolder) state.activeWorkspace.meta_info = parentInfo?.meta_info;
      }
      state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
      state.loading.moveFileToTrash = false;
    });

    builder.addCase(moveFileToTrash.rejected, (state) => {
      state.loading.moveFileToTrash = false;
      message.error('Unable to delete File', 'Please Try again');
    });

    // builder for updating the sidebar position
    builder.addCase(updateSidebarPosition.rejected, (state) => {
      state.loading.filePositionError = true;
      message.error('Error happened while drag-n-drop. Please refresh and try again');
    });

    builder.addCase(addOrganization.pending, (state) => {
      state.loading.addOrganization = true;
    });
    builder.addCase(addOrganization.fulfilled, (state, action) => {
      state.loading.addOrganization = false;
    });
    builder.addCase(addOrganization.rejected, (state, action) => {
      state.loading.addOrganization = false;
      Notification.error('Error happened while adding organization', action?.payload?.error);
    });
    builder.addCase(fetchOrganization.pending, (state) => {
      state.loading.fetchOrganization = true;
    });
    builder.addCase(fetchOrganization.fulfilled, (state, action) => {
      state.loading.fetchOrganization = false;
      const data = action?.payload?.data;
      state.userOrganization = data?.data;
      const workspaces = data?.data?.workspaces;
      state.workspaceList = workspaces?.filter(
        (item) => item?.type !== 'PRIVATE' || item?.created_by === action?.payload?.email
      );
    });
    builder.addCase(fetchOrganization.rejected, (state, action) => {
      state.loading.fetchOrganization = false;
      Notification.error('Error happened while fetching organization', action?.payload?.error);
    });
    builder.addCase(fetchUserOrgMapping.pending, (state) => {
      state.loading.fetchUserOrgMapping = true;
    });
    builder.addCase(fetchUserOrgMapping.fulfilled, (state, action) => {
      state.loading.fetchUserOrgMapping = false;
      state.userOrganization = action?.payload?.data;
    });
    builder.addCase(fetchUserOrgMapping.rejected, (state, action) => {
      state.loading.fetchUserOrgMapping = false;
      Notification.error('Error happened while fetching user organization', action?.payload?.error);
    });

    builder.addCase(moveFileToDestination.pending, (state) => {
      state.moveFileToDestination = true;
    });

    builder.addCase(moveFileToDestination.fulfilled, (state, action) => {
      const {
        convertedParentToFile,
        srcConversionData,
        convertedFolderId,
        desConversionRes,
        convertedFileToFolder,
        updateResult,
        isActiveFileMoved,
        isSourceFile,
        isSourceApiFile,
      } = action.payload;
      const foldersTree = current(state.workspaceFoldersList);
      let foldersList = foldersTree;
      if (srcConversionData && convertedParentToFile) {
        const var1 = parent2FileConversionBuilder({
          foldersTree: foldersList,
          conversionData: srcConversionData,
          movedRes: undefined,
          convertedParentToFile,
          convertedFolderId,
        });
        foldersList = var1.foldersList;
        const { isParentFolder, parentInfo } = var1;
        if (convertedParentToFile && parentInfo && !isParentFolder)
          state.activeWorkspace.meta_info = parentInfo?.meta_info;
      }
      const { foldersList: updatedFoldersList } = file2FolderConversionBuilder({
        foldersTree: foldersList,
        conversionRes: desConversionRes,
        movedItemRes: updateResult,
        convertedFileToFolder,
        isSourceFile,
        isSourceApiFile,
      });
      foldersList = updatedFoldersList;
      if (isActiveFileMoved) {
        state.activeFile = null;
        state.lastFileSaveAt = null;
      }
      state.workspaceFoldersList = foldersList;
      state.folderMappings = constructFolderAndFileMappings(foldersList, {});
      state.loading.moveFileToDestination = false;
    });

    builder.addCase(moveFileToDestination.rejected, (state) => {
      state.loading.moveFileToDestination = false;
    });

    builder.addCase(createUserOrgMapping.pending, (state) => {
      state.loading.createUserOrgMapping = true;
    });
    builder.addCase(createUserOrgMapping.fulfilled, (state, action) => {
      state.loading.createUserOrgMapping = false;
      state.userOrganization = action.payload?.data;
      state.activeWorkspace = {};
      state.activeFile = {};
      state.lastFileSaveAt = null;
      state.workspaceFoldersList = [];
      state.workspaceFilesList = [];
      state.folderMappings = null;
    });
    builder.addCase(createUserOrgMapping.rejected, (state, action) => {
      state.loading.createUserOrgMapping = false;
      Notification.error('Error happened while joining organization', action?.payload?.error);
    });
    builder.addCase(fetchOrganizationList.pending, (state) => {
      state.loading.fetchOrganizationList = true;
    });
    builder.addCase(fetchOrganizationList.fulfilled, (state, action) => {
      state.loading.fetchOrganizationList = false;
      state.organizationList = action?.payload?.data;
    });
    builder.addCase(fetchOrganizationList.rejected, (state, action) => {
      state.loading.fetchOrganizationList = false;
      Notification.error('Error happened while fetching organization list', action?.payload?.error);
    });
    builder.addCase(fetchPaginatedWorkspaceList.pending, (state) => {
      state.loading.paginatedWorkspaceList = true;
    });

    builder.addCase(fetchPaginatedWorkspaceList.fulfilled, (state, action) => {
      state.loading.paginatedWorkspaceList = false;

      const newWorkspaces = action.payload.data;

      const sortByGitBased = (items) => {
        return [...items].sort((a, b) => {
          if (a?.is_git_based === b.is_git_based) return 0;

          if (a?.is_git_based && !b.is_git_based) return 1;

          if (!a?.is_git_based && b.is_git_based) return -1;
          return 0;
        });
      };

      // Sort each category in the workspaces
      const sortedWorkspaces = Object.fromEntries(
        Object.entries(newWorkspaces?.workspaces).map(([key, value]) => [key, sortByGitBased(value)])
      );

      state.newWorkspaceList = sortedWorkspaces;
      const tempWorkspaceList = Object.values(newWorkspaces?.workspaces).flat();
      state.paginatedWorkspaceList = tempWorkspaceList;
      state.workspaceList = tempWorkspaceList;
    });

    builder.addCase(fetchPaginatedWorkspaceList.rejected, (state, action) => {
      state.loading.paginatedWorkspaceList = false;
      Notification.error('Error happened while fetching workspace list', action?.payload?.error);
    });
    builder.addCase(fetchPrivateWorkspaceList.pending, (state) => {
      state.loading.paginatedWorkspaceList = true;
    });

    builder.addCase(fetchPrivateWorkspaceList.fulfilled, (state, action) => {
      state.loading.paginatedWorkspaceList = false;
      const newWorkspaces = action.payload.data;

      if (action.meta.arg.skip === 0) {
        state.privateWorkspaceList = newWorkspaces;
      } else {
        state.privateWorkspaceList = [...state.privateWorkspaceList, ...newWorkspaces];
      }

      state.hasMore = newWorkspaces.length === action.meta.arg.limit;
    });

    builder.addCase(fetchPrivateWorkspaceList.rejected, (state, action) => {
      state.loading.paginatedWorkspaceList = false;
      Notification.error('Error happened while fetching workspace list', action?.payload?.error);
    });
    builder.addCase(fetchPublicWorkspaceList.pending, (state) => {
      state.loading.paginatedWorkspaceList = true;
    });

    builder.addCase(fetchPublicWorkspaceList.fulfilled, (state, action) => {
      state.loading.paginatedWorkspaceList = false;
      const newWorkspaces = action.payload.data;

      if (action.meta.arg.skip === 0) {
        state.publicWorkspaceList = newWorkspaces;
      } else {
        state.publicWorkspaceList = [...state.publicWorkspaceList, ...newWorkspaces];
      }

      state.hasMore = newWorkspaces.length === action.meta.arg.limit;
    });

    builder.addCase(fetchPublicWorkspaceList.rejected, (state, action) => {
      state.loading.paginatedWorkspaceList = false;
      Notification.error('Error happened while fetching workspace list', action?.payload?.error);
    });

    builder.addCase(fetchBitbucketFile.pending, (state) => {
      state.loading.fetchBitbucketFile = true;
    });
    builder.addCase(fetchBitbucketFile.fulfilled, (state, action) => {
      state.loading.fetchBitbucketFile = false;
      state.activeFile = action?.payload;
      state.lastFileSaveAt = action?.payload?.updated_at || null;
    });
    builder.addCase(fetchBitbucketFile.rejected, (state, action) => {
      state.loading.fetchBitbucketFile = false;
      Notification.error('Error happened while fetching bitbucket file', action?.payload?.error);
    });
    builder.addCase(fetchFileById.pending, (state) => {
      state.loading.fetchFileById = true;
    });
    builder.addCase(fetchFileById.fulfilled, (state, action) => {
      state.loading.fetchFileById = false;
      state.activeFile = action?.payload?.data;
      if (action?.payload?.data && Object.keys(action?.payload?.data ?? {}).includes('openapi')) {
        state.openApispec = { ...state.openApispec, [action.payload.data.id]: action.payload.data.openapi };
      }
      state.lastFileSaveAt = action?.payload?.data?.updated_at || null;
      state.workspaceFoldersList = saveFileRecursively(current(state.workspaceFoldersList), action?.payload?.data);
    });
    builder.addCase(fetchFileById.rejected, (state, action) => {
      state.loading.fetchFileById = false;
      state.requestAccessError = action?.payload?.error?.includes('No permissions found');
      Notification.error('Error happend while fetching file', action?.payload?.error);
    });
    builder.addCase(fetchFileDetail.fulfilled, (state, action) => {
      state.fileDetail = action.payload?.data;
      state.lastFileSaveAt = action.payload?.data?.updated_at ?? null;
    });
    builder.addCase(fetchFileDetail.rejected, (state, action) => {
      Notification.error('Something went Wrong while fetching folder');
    });
    builder.addCase(updateBitbucketFiles.pending, (state) => {
      state.loading.updateBitbucketFiles = true;
    });
    builder.addCase(updateBitbucketFiles.fulfilled, (state, action) => {
      state.loading.updateBitbucketFiles = false;
    });
    builder.addCase(updateBitbucketFiles.rejected, (state, action) => {
      state.loading.updateBitbucketFiles = false;
      Notification.error('Error happened while updating bitbucket files', action?.payload?.error);
    });
    builder.addCase(fetchBitbucketFilesInFolder.pending, (state) => {
      state.loading.fetchBitbucketFilesInFolder = true;
    });
    builder.addCase(fetchBitbucketFilesInFolder.fulfilled, (state, action) => {
      state.loading.fetchBitbucketFilesInFolder = false;
      state.workspaceFoldersList = buildBitBucketTree(current(state.workspaceFoldersList) || [], action?.payload?.data);
    });
    builder.addCase(fetchBitbucketFilesInFolder.rejected, (state, action) => {
      state.loading.fetchBitbucketFilesInFolder = false;
      Notification.error('Error happened while fetching bitbucket files', action?.payload?.error);
    });

    builder
      .addCase(uploadOpenApiToServer.pending, (state) => {
        state.loading.uploadOpenApiToServer = true;
      })
      .addCase(uploadOpenApiToServer.fulfilled, (state, action) => {
        const updatedFileData = action?.payload?.data;
        state.activeFile = updatedFileData;
        state.lastFileSaveAt = updatedFileData?.updated_at ?? null;
        state.workspaceFoldersList = createFileRecursively(state?.workspaceFoldersList, updatedFileData);
        state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
        state.loading.uploadOpenApiToServer = false;
      })
      .addCase(uploadOpenApiToServer.rejected, (state, action) => {
        state.loading.uploadOpenApiToServer = false;
      });

    builder
      .addCase(updatePublicationForWorkspace.pending, (state) => {
        state.loading.updatePublicationForWorkspace = true;
      })
      .addCase(updatePublicationForWorkspace.fulfilled, (state, action) => {
        const tempWorkspaceList = Object.values(state.workspaceList).flat();
        state.workspaceList = state.workspaceList.map((workspace) => {
          if (workspace.id === action?.payload?.data?.id) {
            return action?.payload?.data;
          }
          return workspace;
        });
        state.loading.updatePublicationForWorkspace = false;
      })
      .addCase(updatePublicationForWorkspace.rejected, (state) => {
        state.loading.updatePublicationForWorkspace = false;
      });

    builder
      .addCase(updateOpenApiFileToServer.pending, (state) => {
        state.loading.updateOpenApiFileToServer = true;
      })
      .addCase(updateOpenApiFileToServer.fulfilled, (state, action) => {
        state.workspaceFoldersList = saveFileRecursively(state?.workspaceFoldersList, action?.payload?.data);
        state.folderMappings = constructFolderAndFileMappings(state.workspaceFoldersList, {});
        state.activeFile = action?.payload?.data;
        state.lastFileSaveAt = action.payload?.data?.updated_at;
        state.loading.updateOpenApiFileToServer = false;
      })
      .addCase(updateOpenApiFileToServer.rejected, (state) => {
        state.loading.updateOpenApiFileToServer = false;
      });
    builder.addCase(deleteWorkspace.pending, (state) => {
      state.loading.deleteWorkspace = true;
    });
    builder.addCase(deleteWorkspace.fulfilled, (state, action) => {
      state.activeWorkspace = null;
      state.activeFile = null;
      state.lastFileSaveAt = null;
      state.workspaceFoldersList = [];
      const workspaceId = action.payload;
      Object.keys(state.newWorkspaceList).forEach((type) => {
        const workspaces = state.newWorkspaceList[type];
        if (Array.isArray(workspaces)) {
          state.newWorkspaceList[type] = workspaces.filter((workspace) => workspace.id !== workspaceId);
        }
      });
      state.loading.deleteWorkspace = false;
      Notification.success('Workspace successfully deleted.');
    });
    builder.addCase(deleteWorkspace.rejected, (state, action) => {
      state.loading.deleteWorkspace = false;
      Notification.error('Error happened while deleting workspace', action?.payload?.error);
    });

    builder.addCase(enableCIOpenApi.pending, (state, action) => {
      state.loading.enableCIOpenApi = true;
    });
    builder.addCase(enableCIOpenApi.fulfilled, (state, action) => {
      state.loading.enableCIOpenApi = false;
      state.activeFile = action?.payload?.data;
    });
    builder.addCase(enableCIOpenApi.rejected, (state, action) => {
      state.loading.enableCIOpenApi = false;
      Notification.error('Unable to enable CI/CD for api', action?.payload?.error);
    });
    builder.addCase(fetchRecentWorkspaces.pending, (state, action) => {
      state.loading.recentWorkspaces = true;
    });
    builder.addCase(fetchRecentWorkspaces.fulfilled, (state, action) => {
      state.loading.recentWorkspaces = false;
      state.recentWorkspaces = action?.payload?.data;
    });
    builder.addCase(fetchRecentWorkspaces.rejected, (state, action) => {
      state.loading.recentWorkspaces = false;
      Notification.error('Unable to fetch recent workspaces', action?.payload?.error);
    });
    builder.addCase(fetchStarredWorkspace.pending, (state, action) => {
      state.loading.starredWorkspace = true;
    });
    builder.addCase(fetchStarredWorkspace.fulfilled, (state, action) => {
      state.loading.starredWorkspace = false;
      state.starredWorkspace = action?.payload?.data;
    });
    builder.addCase(fetchStarredWorkspace.rejected, (state, action) => {
      state.loading.starredWorkspace = false;
      Notification.error('Unable to fetch starred workspaces', action?.payload?.error);
    });
    builder.addCase(addStarredWorkspace.fulfilled, (state, action) => {
      Notification.success('Added to favorites');
    });
    builder.addCase(addStarredWorkspace.rejected, (state, action) => {
      Notification.error('Unable to star workspace', action?.payload?.error);
    });
    builder.addCase(unstarWorkspace.fulfilled, (state, action) => {
      Notification.success('Removed from favorites');
    });
    builder.addCase(unstarWorkspace.rejected, (state, action) => {
      Notification.error('Unable to unstar workspace', action?.payload?.error);
    });
    builder.addCase(fetchFiles.pending, (state, action) => {
      const type = action?.meta?.arg?.type;
      switch (type) {
        case DOCUMENTS_TYPE.OWNED:
          state.loading.ownedFilesResult = true;
          break;
        case DOCUMENTS_TYPE.RECENTLY_EDIT:
          state.loading.recentlyEditedFilesResult = true;
          break;
        default:
          state.loading.filesResult = true;
      }
    });
    builder.addCase(fetchFiles.fulfilled, (state, action) => {
      const type = action?.meta?.arg?.type;
      switch (type) {
        case DOCUMENTS_TYPE.OWNED:
          state.loading.ownedFilesResult = false;
          state.ownedFilesResult = {
            total: action?.payload?.data?.total_count,
            data: action?.payload?.data?.files,
          };
          break;
        case DOCUMENTS_TYPE.RECENTLY_EDIT:
          state.loading.recentlyEditedFilesResult = false;
          state.recentlyEditedFilesResult = {
            total: action?.payload?.data?.total_count,
            data: action?.payload?.data?.files,
          };
          break;
        default:
          state.loading.filesResult = false;
          state.filesResult = {
            total: action?.payload?.data?.total_count,
            data: action?.payload?.data?.files,
          };
      }
    });
    builder.addCase(fetchFiles.rejected, (state, action) => {
      const type = action?.meta?.arg?.type;
      switch (type) {
        case DOCUMENTS_TYPE.OWNED:
          state.loading.ownedFilesResult = false;
          break;
        case DOCUMENTS_TYPE.RECENTLY_EDIT:
          state.loading.recentlyEditedFilesResult = false;
          break;
        default:
          state.loading.filesResult = false;
      }
      Notification.error('Error happened while fetching files', action?.payload?.error);
    });
    builder.addCase(autoSaveFile.fulfilled, (state, action) => {
      state.lastFileSaveAt = action.payload.data?.updated_at;
    });
    builder.addCase(changeActiveFileMetaInfo.fulfilled, (state, action) => {
      const updatedFileData = action?.payload?.data;
      state.workspaceFoldersList = updateFileAndMaybeFolder(current(state.workspaceFoldersList), updatedFileData);
    });
  },
});

export const fetchOrganization = createAsyncThunk('cms/fetchOrganization', async (organization_id, thunkAPI) => {
  const { rejectWithValue } = thunkAPI;
  try {
    const state = thunkAPI.getState();
    const email = state.user.user.email;
    const response = await clientService.get(`${ORGANIZATION_URL}/${organization_id}`);
    return { data: response, email: email };
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const fetchWorkspaceByType = createAsyncThunk(
  'cms/fetchWorkspaceByType',
  async ({ type, skip, limit, organizationId }, { rejectWithValue }) => {
    try {
      const URL = type?.toUpperCase() === 'PRIVATE' ? `${WORKSPACE_URL}/private` : WORKSPACE_URL;
      const response = await clientService.get(URL, {
        params: { type, skip, limit, organization_id: organizationId },
      });
      return response;
    } catch (error) {
      console.log(error);
      return rejectWithValue({ error: error?.data?.error?.message });
    }
  }
);

export const fetchPaginatedWorkspaceList = createAsyncThunk('cms/fetchworkspace', async (p, { rejectWithValue }) => {
  try {
    const response = await clientService.get(WORKSPACE_URL, {
      params: p,
    });

    return response;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});
export const fetchPublicWorkspaceList = createAsyncThunk(
  'cms/fetchPublicWorkspace',
  async (params, { rejectWithValue }) => {
    try {
      const response = await clientService.get(WORKSPACE_URL, { params: params });
      return response;
    } catch (error) {
      return rejectWithValue({ error: error?.data?.error?.message });
    }
  }
);
export const fetchPrivateWorkspaceList = createAsyncThunk(
  'cms/fetchPrivatecWorkspace',
  async (params, { rejectWithValue }) => {
    try {
      const response = await clientService.get(WORKSPACE_URL, { params: params });
      return response;
    } catch (error) {
      return rejectWithValue({ error: error?.data?.error?.message });
    }
  }
);
export const fetchOrganizationList = createAsyncThunk(
  'cms/fetchOrganizationList',
  async (username, { rejectWithValue }) => {
    try {
      const response = await clientService.get(`${ORGANIZATION_URL}`);
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const getOrganizationList = createAsyncThunk(
  'cms/fetchOrganizationList',
  async (username, { rejectWithValue }) => {
    try {
      const response = await clientService.get(`onedoc_backend/v1/user/${username}/organizations`);
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const addOrganization = createAsyncThunk('cms/addOrganization', async (body, { rejectWithValue }) => {
  try {
    const response = await clientService.post(ORGANIZATION_URL, body);
    return response;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const fetchUserOrgMapping = createAsyncThunk('cms/fetchUserOrgMapping', async (params, { rejectWithValue }) => {
  try {
    const response = await clientService.get(USER_ORG_MAPPING_URL, {
      params,
    });
    return response;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const createUserOrgMapping = createAsyncThunk(
  'cms/createUserOrgMapping',
  async ({ body, showNotification }, { rejectWithValue }) => {
    try {
      let response = await clientService.get(USER_ORG_MAPPING_URL, {
        params: { username: body?.username, organization_id: body?.organization_id },
      });
      if (response.data && typeof response.data === 'object' && Object.keys(response.data).length === 0) {
        response = await clientService.post(USER_ORG_MAPPING_URL, body);
      }
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const fetchAllWorkspaces = createAsyncThunk('cms/fetchAllWorkspaces', async (params, { rejectWithValue }) => {
  try {
    const response = await clientService.get(`${WORKSPACE_URL}`, {
      params: params,
    });
    return response;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const fetchAllFolders = createAsyncThunk(
  'cms/fetchAllFolders',
  async (workspace_id, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await clientService.get(`${WORKSPACE_URL}/${workspace_id}`, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (err) {
      if (err?.data?.error?.status === 'NOT_FOUND') {
        const organizationId = getState()?.organization?.userOrganization?.id;
        const p = { organization_id: organizationId };
        dispatch(fetchPaginatedWorkspaceList(p));
      }

      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const fetchSpecficFolder = createAsyncThunk(
  'cms/fetchSecificFolder',
  async (workspace_id, { rejectWithValue }) => {
    try {
      const response = await clientService.get(`${WORKSPACE_URL}/${workspace_id}`, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (error) {}
  }
);

export const fetchPrivateWorkspace = createAsyncThunk('cms/fetchPrivateWorkspace', async (_, { rejectWithValue }) => {
  // No parameters, just an underscore for unused argument
  try {
    const response = await clientService.get(`${WORKSPACE_URL}/private`);

    return response.data; // Return data on success
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'An error occurred'); // Safely handle error
  }
});

export const moveFolderToWorkspace = createAsyncThunk(
  'cms/moveFolderToWorkspace',
  async ({ body, folder_id, workspace_id }, { rejectWithValue }) => {
    try {
      const response = await clientService.put(`${FOLDER_URL}/${folder_id}/move`, body, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (error) {
      Notification.error('Unable to move Folder');
    }
  }
);
export const fetchAllFiles = createAsyncThunk(
  'cms/fetchAllFiles',
  async ({ folder_id, workspace_id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await clientService.get(`${FOLDER_URL}/${folder_id}`, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (err) {
      if (err?.data?.error?.status === 'NOT_FOUND') {
        dispatch(fetchAllFolders(workspace_id));
      }
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);
export const fetchStarredFiles = createAsyncThunk(
  'cms/fetchStarredFiles',
  async ({ workspace_id, skip = 0, limit = 50 }, { rejectWithValue }) => {
    try {
      const response = await clientService.get(`${STARRED_FILE_URL}/starred`, {
        params: {
          skip,
          limit,
          workspace_id,
        },
      });

      return response;
    } catch (error) {
      return rejectWithValue({ error: error?.data?.error?.message });
    }
  }
);
export const addStarredFile = createAsyncThunk(
  'cms/addStarredFile',
  async ({ id, workspace_id }, { rejectWithValue }) => {
    try {
      const response = await clientService.put(`${FILE_URL}/${id}/star`, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (error) {
      console.log(error);
      return rejectWithValue({ error: error?.data?.error?.message });
    }
  }
);
export const unstarFile = createAsyncThunk('cms/unstarFile', async ({ workspace_id, id }, { rejectWithValue }) => {
  try {
    const response = await clientService.put(`${FILE_URL}/${id}/unstar`, {
      headers: genWorkspaceHeader(workspace_id),
    });
  } catch (error) {
    console.log(error);
    return rejectWithValue({ error: error?.data?.error?.message });
  }
});

export const createWorkspace = createAsyncThunk(
  'cms/createWorkspace',
  async ({ data, params }, { dispatch, rejectWithValue }) => {
    try {
      const response = await clientService.post(`${WORKSPACE_URL}`, data);
      await dispatch(fetchAllWorkspaces(params));
      await dispatch(fetchPrivateWorkspace());
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const createFolder = createAsyncThunk('cms/createFolder', async (data, { rejectWithValue }) => {
  try {
    const response = await clientService.post(`${FOLDER_URL}`, data, {
      headers: genWorkspaceHeader(data?.workspace_id),
    });
    return response;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const createFile = createAsyncThunk('cms/createFile', async (data, { rejectWithValue, getState }) => {
  try {
    const { folderMappings } = getState().organization;
    const fileId = data?.folder_id;
    const { convertedFileToFolder, ...conversionRes } = await convertFileToFolder({
      id: fileId,
      workspaceId: data?.workspace_id,
      folderMappings,
    });
    const createFileRes = await clientService.post(
      `${FILE_URL}`,
      { ...data, folder_id: convertedFileToFolder ? conversionRes?.id : fileId },
      { headers: genWorkspaceHeader(data?.workspace_id) }
    );
    return { fileRes: createFileRes?.data, convertedFileToFolder, conversionRes };
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const updateWorkspace = createAsyncThunk(
  'cms/updateWorkspace',
  async ({ body, params }, { rejectWithValue }) => {
    const workspace_id = params;
    try {
      const response = await clientService.put(`${WORKSPACE_URL}/${params}`, body, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const updateWorkspaceType = createAsyncThunk(
  'cms/updateWorkspaceType',

  async ({ body, params }, { rejectWithValue }) => {
    const { workspace_id } = params;

    try {
      const response = await clientService.put(`${WORKSPACE_URL}/${workspace_id}`, body, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue({ error: error?.data?.error?.message });
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'cms/deleteFolder',
  async ({ id, workspaceId }, { rejectWithValue, getState }) => {
    const { workspaceFoldersList, activeFile, folderMappings } = getState().organization;
    const parentFolder = folderMappings[id]?.parent_id_id;
    try {
      const { convertedParentToFile, conversionData, convertedFolderId } = convertFolderToFile({
        id: parentFolder?.id,
        workspaceId,
        folderMappings,
      });
      await clientService.delete(`${FOLDER_URL}/${id}`, { headers: genWorkspaceHeader(workspaceId) });
      const isActiveFileDeleted = findFileInTargetFolder(workspaceFoldersList, id, activeFile?.id);
      if (isActiveFileDeleted) {
        window.history.replaceState(null, '', `/space/${workspaceId}`);
      }
      return { convertedParentToFile, deleteFolderRes: { id }, conversionData, isActiveFileDeleted, convertedFolderId };
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const moveFileToTrash = createAsyncThunk(
  'cms/moveFileToTrash',
  async ({ id, workspaceId }, { rejectWithValue, getState }) => {
    const { activeFile, folderMappings } = getState().organization;
    const isSameFileRemoved = id === activeFile?.id;
    const fileParent = folderMappings[id]?.folder_id;

    try {
      const { convertedParentToFile, conversionData, convertedFolderId } = await convertFolderToFile({
        id: fileParent,
        workspaceId,
        folderMappings,
      });
      let deleteFileRes = null;
      const file = folderMappings[id];
      const isOpenApi = (file?.type && file?.type === 'openapi_spec_file') || Object.keys(file).includes('openapi');
      if (isOpenApi) {
        deleteFileRes = await clientService.put(
          `${UPLOAD_OPENAPI}/${id}`,
          { status: fileStatus.REMOVED },
          { headers: genWorkspaceHeader(workspaceId) }
        );
      } else
        deleteFileRes = await clientService.put(
          `${FILE_URL}/${id}`,
          { status: fileStatus.REMOVED },
          { headers: genWorkspaceHeader(workspaceId) }
        );

      if (isSameFileRemoved && window) window.history.replaceState(null, '', `/space/${workspaceId}`);
      return {
        isSameFileRemoved,
        fileRes: deleteFileRes?.data,
        convertedParentToFile,
        conversionRes: conversionData,
        convertedFolderId,
      };
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const moveFileToDestination = createAsyncThunk(
  'cms/moveFileToDestination',
  async ({ sourceId, destinationId, position, navigate }, { rejectWithValue, getState }) => {
    const { folderMappings, activeWorkspace, workspaceFoldersList, activeFile } = getState().organization;

    const source = folderMappings[sourceId];
    const destination = activeWorkspace?.id === destinationId ? null : folderMappings[destinationId];
    if (!source || destination === undefined)
      rejectWithValue({ error: 'Invalid source or destination during drag-n-drop' });

    const workspaceId = activeWorkspace?.id;
    // Check source item type
    const isSourceApiFile = isOpenApiFile(source);
    const isSourceFile = !isFolder(source) && !isSourceApiFile;
    const isDestinationFile = destination && !isFolder(destination) ? true : false;

    let destinationConversionResult = null;
    try {
      if (isDestinationFile) {
        destinationConversionResult = await convertFileToFolder({ id: destinationId, workspaceId, folderMappings });
        destinationId = destinationConversionResult?.id;
        position = 0;
      }

      const url = isSourceFile ? FILE_URL : isSourceApiFile ? UPLOAD_OPENAPI : FOLDER_URL;
      const parentKey = isSourceFile || isSourceApiFile ? 'folder_id' : 'parent_id';
      const data =
        !isSourceFile && !isSourceApiFile
          ? { title: source?.title, meta_info: source?.meta_info, icon_id: source?.icon_id, workspace_id: workspaceId }
          : {};
      const updateResult = await clientService.put(
        `${url}/${sourceId}`,
        { ...data, [parentKey]: destination === null ? null : destinationId },
        { headers: genWorkspaceHeader(workspaceId) }
      );

      const sourceParentId = isSourceFile ? source?.folder_id : source?.parent_id_id;
      const srcConversionRes = await convertFolderToFile({ id: sourceParentId, workspaceId, folderMappings });
      const { convertedFileToFolder, ...desConversionRes } = destinationConversionResult || {};
      let isActiveFileMoved = activeFile?.id === sourceId || srcConversionRes?.convertedParentToFile;

      if (!isSourceFile || isSourceApiFile) {
        isActiveFileMoved =
          srcConversionRes?.convertedParentToFile ||
          findFileInTargetFolder(workspaceFoldersList, sourceId, activeFile?.id);
      }
      if (isActiveFileMoved && window) window.history.replaceState(null, '', `/space/${workspaceId}`);
      return {
        convertedParentToFile: srcConversionRes?.convertedParentToFile,
        srcConversionData: srcConversionRes?.conversionData,
        convertedFolderId: srcConversionRes?.convertedFolderId,
        desConversionRes,
        convertedFileToFolder,
        updateResult: updateResult?.data,
        isActiveFileMoved,
        isSourceFile,
        isSourceApiFile,
      };
    } catch (error) {
      console.log(error);
      return rejectWithValue({ error: 'Some error occurred during the drag-n-drop operation.' });
    }
  }
);

export const updateFolder = createAsyncThunk(
  'cms/updateFolder',
  async ({ data, folder_id, workspace_id }, { rejectWithValue }) => {
    try {
      const response = await clientService.put(`${FOLDER_URL}/${folder_id}`, data, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const moveFileToWorkspace = createAsyncThunk(
  'cms/moveFiletoWorkspace',
  async ({ data, file_id, workspace_id, autosave }, { rejectWithValue }) => {
    try {
      const response = await clientService.put(`${FILE_URL}/${file_id}`, data, {
        headers: genWorkspaceHeader(workspace_id),
      });
      return { ...response, autosave };
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);
export const copyFileToWorkspace = createAsyncThunk('cms/copyFileToWorkspace', async (data, { rejectWithValue }) => {
  try {
    const response = await clientService.post(`${FILE_URL}`, data, { headers: genWorkspaceHeader(data?.workspace_id) });
    return response;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const updateFile = createAsyncThunk(
  'cms/updateFile',
  async ({ data, file_id, workspace_id, autosave, updateActiveFile = true }, { getState, rejectWithValue }) => {
    try {
      const updateTitleOnly = Object.keys(data).length === 1 && Object.keys(data)[0] === 'title';
      const workspace_content = getState().organization.workspaceFoldersList;
      const file = findFileInRecursiveFolder(workspace_content, file_id);
      if ((file?.type && file?.type === 'openapi_spec_file') || Object.keys(file).includes('openapi')) {
        const response = await clientService.put(
          `${UPLOAD_OPENAPI}/${file_id}`,
          { ...data },
          { headers: genWorkspaceHeader(workspace_id) }
        );

        return { ...response, autosave, updateActiveFile };
      }
      const response = await clientService.put(`${FILE_URL}/${file_id}`, data, {
        headers: genWorkspaceHeader(workspace_id),
      });
      return { ...response, autosave, updateActiveFile, updateTitleOnly };
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const updateSidebarPosition = createAsyncThunk(
  'cms/updateSidebarPosition',
  async ({ id, position }, { rejectWithValue, getState }) => {
    const { folderMappings } = getState().organization;
    const item = folderMappings[id];
    if (!item) rejectWithValue({ error: 'Item not found' });
    const workspaceId = item?.workspace_id;
    try {
      const isFolder = 'parent_id_id' in item ? true : false;
      const isApiFile = item?.type === 'openapi_spec_file';
      let response = null;
      if (isFolder) {
        response = await clientService.put(
          `${FOLDER_URL}/${id}`,
          { sidebar_position: position },
          { headers: genWorkspaceHeader(workspaceId) }
        );
      } else if (!isApiFile) {
        response = await clientService.put(
          `${FILE_URL}/${id}`,
          { sidebar_position: position },
          { headers: genWorkspaceHeader(workspaceId) }
        );
      } else {
        response = await clientService.put(
          `${UPLOAD_OPENAPI}/${id}`,
          { sidebar_position: position },
          { headers: genWorkspaceHeader(workspaceId) }
        );
      }
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const deleteWorkspace = createAsyncThunk(
  'cms/deleteWorkspace',
  async ({ workspaceId, params }, { dispatch, rejectWithValue }) => {
    try {
      const response = await clientService.put(`${WORKSPACE_URL}/${workspaceId}/delete`, {
        headers: genWorkspaceHeader(workspaceId),
      });

      if (response.is_success) {
        return workspaceId;
      }
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const fetchBitbucketFile = createAsyncThunk(
  'cms/fetchBitbucketFile',
  async ({ params, file }, { rejectWithValue }) => {
    try {
      const response = await clientService.get(SCM_FILE_URL, { params });
      return { ...file, data: response };
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const fetchFileById = createAsyncThunk(
  'cms/fetchFileById',
  async ({ id, isApiFile, workspace_id }, { rejectWithValue }) => {
    try {
      const uri = isApiFile ? UPLOAD_OPENAPI : FILE_URL;
      const response = await clientService.get(`${uri}/${id}/by_ref`, {
        headers: genWorkspaceHeader(workspace_id),
      });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);
export const fetchFileDetail = createAsyncThunk(
  'cms/fetchFileDetail',
  async ({ id, workspace_id }, { rejectWithValue }) => {
    try {
      const url = isApiFile ? UPLOAD_OPENAPI : FILE_URL;
      const response = await clientService.get(`${url}/${id}`, {
        headers: genWorkspaceHeader(workspace_id),
      });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const updateBitbucketFiles = createAsyncThunk(
  'cms/updateBitbucketFiles',
  async ({ body, workspace_id, autosave }, { dispatch, rejectWithValue }) => {
    try {
      const response = await clientService.post(SCM_FILE_URL, body);
      await dispatch(fetchAllFolders(workspace_id));

      return { response, autosave };
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const fetchBitbucketFilesInFolder = createAsyncThunk(
  'cms/fetchBitbucketFilesInFolder',
  async (params, { rejectWithValue }) => {
    try {
      const response = await clientService.get(SCM_FOLDER_URL, { params });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const uploadOpenApiToServer = createAsyncThunk(
  'cms/uploadOpenApiToServer',
  async ({ workspace_id, ...rest }, { rejectWithValue }) => {
    try {
      const openapiBody = { ...rest, workspace_id };
      const response = await clientService.post(UPLOAD_OPENAPI, openapiBody, {
        headers: genWorkspaceHeader(workspace_id),
      });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const updatePublicationForWorkspace = createAsyncThunk(
  'cms/updatePublicationForWorkspace',
  async ({ workspace_id, publication_type }, { rejectWithValue }) => {
    try {
      const response = await clientService.put(
        `${WORKSPACE_URL}/${workspace_id}`,
        { publication_type },
        {
          headers: genWorkspaceHeader(workspace_id),
        }
      );
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const updateOpenApiFileToServer = createAsyncThunk(
  'cms/updateOpenApiFileToServer',
  async (
    { id, created_at, created_by, updated_at, updated_by, webhook_url, workspace_id, ...openApiBody },
    { rejectWithValue }
  ) => {
    try {
      delete openApiBody['file_owner'];
      delete openApiBody['can_edit'];
      const response = await clientService.put(`${UPLOAD_OPENAPI}/${id}`, openApiBody, {
        headers: genWorkspaceHeader(workspace_id),
      });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const enableCIOpenApi = createAsyncThunk(
  'cms/enableCIOpenApi',
  async ({ id, body, workspaceId }, { rejectWithValue }) => {
    try {
      const response = await clientService.put(`${UPLOAD_OPENAPI}/${id}`, body, {
        headers: genWorkspaceHeader(workspaceId),
      });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const changeActiveFileMetaInfo = createAsyncThunk(
  'cms/changeActiveFileMetaInfo',
  async ({ id, metaInfo }, { rejectWithValue, getState }) => {
    const { activeFile } = getState()?.organization;
    try {
      const prevMetaInfo = activeFile?.meta_info || {};
      const response = await clientService.put(`${FILE_URL}/${id}`, { meta_info: { ...prevMetaInfo, ...metaInfo } });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const autoSaveFile = createAsyncThunk(
  'cms/autoSaveFile',
  async ({ id, body }, { rejectWithValue, getState }) => {
    try {
      const workspaceId = getState().organization?.activeWorkspace?.id;
      const response = await clientService.put(`${FILE_URL}/${id}`, body, { headers: genWorkspaceHeader(workspaceId) });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const fetchFiles = createAsyncThunk('cms/fetchFiles', async ({ params, type }, { rejectWithValue }) => {
  try {
    const response = await clientService.get(STARRED_FILE_URL, {
      params: { ...params, serialize: true },
    });
    return response;
  } catch (error) {
    console.log(error);
    return rejectWithValue({ error: error?.data?.error?.message });
  }
});

export const fetchRecentWorkspaces = createAsyncThunk(
  'cms/fetchRecentWorkspaces',
  async ({ params }, { rejectWithValue }) => {
    try {
      const response = await clientService.get(USER_RECENT_ACTIVITY + '/workspaces', {
        params: { ...params },
      });
      return response;
    } catch (error) {
      console.log(error);
      return rejectWithValue({ error: error?.data?.error?.message });
    }
  }
);
export const fetchStarredWorkspace = createAsyncThunk(
  'cms/fetchStarredWorkspace',
  async ({ params }, { rejectWithValue }) => {
    try {
      const response = await clientService.get(WORKSPACE_URL + '/starred', {
        params: { ...params },
      });
      return response;
    } catch (error) {
      return rejectWithValue({ error: error?.data?.error?.message });
    }
  }
);

export const addStarredWorkspace = createAsyncThunk('cms/addStarredWorkspace', async ({ id }, { rejectWithValue }) => {
  try {
    const response = await clientService.put(`${WORKSPACE_API_URL}/${id}/star`, {
      headers: genWorkspaceHeader(id),
    });

    return response;
  } catch (error) {
    return rejectWithValue({ error: error?.data?.error?.message });
  }
});
export const unstarWorkspace = createAsyncThunk('cms/unstarWorkspace', async ({ id }, { rejectWithValue }) => {
  try {
    const response = await clientService.put(`${WORKSPACE_API_URL}/${id}/unstar`, {
      headers: genWorkspaceHeader(id),
    });
    return response;
  } catch (error) {
    return rejectWithValue({ error: error?.data?.error?.message });
  }
});

export default organizationSlice.reducer;
export const {
  setActiveWorkspaceAction,
  removeFileAfterMoving,
  setActiveFileNull,
  setIsMobile,
  updateOpenApiSpec,
  updateSelectionPathOpenApi,
  setCollapseLeftBar,
  addActiveWorkspaceIfNotPresent,
  setLeftbarWidth,
  setIsAutoCollapseLeftbar,
} = organizationSlice.actions;
